import * as React from 'react';
import { Passenger as PassengerSchema } from '../types';
import { Passenger } from './Passenger/Passenger';
import { ModifyPassenger, PassengerType } from '../../enums';
import { useTheme } from '../../theme';
import { useIsMobile } from '../../utils';
import cn from 'classnames';

import { BaseButton } from '../../BaseComponents';
import { useTranslation } from 'react-i18next';
import Slider, { Settings } from 'react-slick';
import { useCallback } from 'react';

interface Props {
	passengers: PassengerSchema[];
	addPassenger?: (payload: ModifyPassenger) => void;
	removePassenger?: (payload: ModifyPassenger) => void;
	fareGroups?: string[];
	setCurrentGroup?: (payload: string) => void;
	currentGroup?: string;
	isSegmentsInternational?: boolean;
}

export const Passengers = React.memo<Props>(
	({
		passengers,
		fareGroups,
		currentGroup,
		setCurrentGroup,
		addPassenger,
		removePassenger,
		isSegmentsInternational
	}) => {
		const { PassengersStyles: css } = useTheme('Passengers');
		const { t } = useTranslation('Passengers');

		const isMobile = useIsMobile();

		const PassengersList = () => {
			return (
				<ul className={css.list}>
					{passengers.map(passenger => (
						<li
							data-pass-type={passenger.type}
							data-test-id="passenger-row"
							data-count={passenger.count}
							key={passenger.type}
							className={cn(css.item, {
								[css.item_FNN]: passenger.type === PassengerType.Teenager
							})}
						>
							<Passenger
								passenger={passenger}
								addPassenger={addPassenger}
								removePassenger={removePassenger}
								decreaseIsEnabled={passenger.canBeDecreased}
								increaseIsEnabled={passenger.canBeIncreased}
								isSegmentsInternational={isSegmentsInternational}
							/>
						</li>
					))}
				</ul>
			);
		};

		if (!fareGroups || fareGroups.length <= 1) {
			return <PassengersList />;
		}

		const sliderSettings: Settings = {
			dots: false,
			infinite: false,
			speed: 500,
			initialSlide: fareGroups?.findIndex(group => group === currentGroup) || 0,
			slidesToShow: 1,
			slidesToScroll: 1,
			adaptiveHeight: true,
			variableWidth: true,
			centerMode: false,
			arrows: true
		};

		const renderFares = useCallback(() => {
			return fareGroups.map(fareGroup => (
				<BaseButton
					onClick={() => {
						setCurrentGroup?.(fareGroup);
					}}
					key={fareGroup}
					variant="text"
					className={cn(css.fareGroup, fareGroup === currentGroup ? css.currentGroup : null)}
				>
					{t(fareGroup)}
				</BaseButton>
			));
		}, [currentGroup]);

		return (
			<div className={css.withGroupsWrapper}>
				<div className={css.groupsWrapper}>
					<div className={css.groupsTitle}>{t('Choose a fare')}</div>
					<div className={css.fareGroups}>
						{isMobile && (
							<Slider {...sliderSettings} className={css.slider}>
								{renderFares()}
							</Slider>
						)}
						{!isMobile && <>{renderFares()}</>}
					</div>
				</div>
				<div className={css.withGroupsDelimiter}></div>
				<div className={css.passengersWrapper}>
					<div className={css.passengersTitle}>{t('Choose passengers')}</div>
					<PassengersList />
				</div>
			</div>
		);
	}
);

export default Passengers;
